import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../services/supabaseClient';
import { integrationService } from '../../integrations/services/integrationService';
import { logout } from '../services/authService';

const AuthContext = createContext(null);

const withTimeout = (promise, timeoutMs = 5000) => {
    return Promise.race([
        promise,
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Operation timed out')), timeoutMs)
        ),
    ]);
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const hasRefreshedRef = useRef(false);
    const currentUserIdRef = useRef(null);

    useEffect(() => {
        supabase.auth
            .getSession()
            .then(async ({ data }) => {
                setUser(data.session?.user ?? null);
                setLoading(false);

                if (data.session?.user) {
                    currentUserIdRef.current = data.session.user.id;
                    hasRefreshedRef.current = false;
                    try {
                        await withTimeout(integrationService.refreshTokensOnLogin(data.session.user.id), 5000);
                        hasRefreshedRef.current = true;
                    } catch (error) {
                        console.error('Failed to refresh tokens on initial load:', error);
                        hasRefreshedRef.current = true;
                    }
                } else {
                    currentUserIdRef.current = null;
                    hasRefreshedRef.current = false;
                }
            })
            .catch((error) => {
                console.error('Error getting session:', error);
                setLoading(false);
            });

        const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
            const newUserId = session?.user?.id ?? null;

            if (event === 'SIGNED_OUT') {
                currentUserIdRef.current = null;
                hasRefreshedRef.current = false;
                setUser(null);
                setLoading(false);
                return;
            }

            if (newUserId !== currentUserIdRef.current) {
                currentUserIdRef.current = newUserId;
                hasRefreshedRef.current = false;
            }

            setUser(session?.user ?? null);
            setLoading(false);

            if (event === 'SIGNED_IN' && session?.user && !hasRefreshedRef.current) {
                hasRefreshedRef.current = true;
                try {
                    await withTimeout(integrationService.refreshTokensOnLogin(session.user.id), 5000);
                } catch (error) {
                    console.error('Failed to refresh tokens on login:', error);
                }
            }
        });

        return () => {
            listener.subscription.unsubscribe();
        };
    }, []);

    const signOut = useCallback(async () => {
        await logout();
        setUser(null);
    }, []);

    const value = useMemo(() => ({ user, loading, signOut }), [user, loading, signOut]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (ctx == null) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
}
